import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { API_ENDPOINTS } from '../constants/api';
import { getSignedUrls } from '../utils/fileOperations';
import { normalizeSkipLimitRequest } from '../utils/listPagination';
import { reportError } from '../sentry';

class FollowService {
  /**
   * Follow a PPV creator
   * @param {string} creatorId - Creator ID to follow
   * @returns {Promise<Object>} Follow response
   */
  async followPPVCreator(creatorId) {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.FOLLOW_PPV_CREATOR}/${creatorId}`, {});
      return ResponseHandler.handleSuccess(response, 'Creator followed successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to follow creator');
    }
  }

  /**
   * Unfollow a PPV creator
   * @param {string} creatorId - Creator ID to unfollow
   * @returns {Promise<Object>} Unfollow response
   */
  async unfollowPPVCreator(creatorId) {
    try {
      const response = await apiClient.delete(`${API_ENDPOINTS.FOLLOW_PPV_CREATOR}/${creatorId}`);
      return ResponseHandler.handleSuccess(response, 'Creator unfollowed successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to unfollow creator');
    }
  }

  /**
   * Fetch one page of followers or subscribers for a content creator.
   * The same endpoint serves both; the creator role decides the label.
   * @param {string} creatorId - Creator ID to fetch followers for
   * @param {{ skip?: number, limit?: number, search?: string }} [params]
   * @returns {Promise<Object>} Followers page plus skip/limit pagination
   */
  async fetchFollowersList(creatorId, params = {}) {
    try {
      const { skip, limit } = normalizeSkipLimitRequest(params);
      const search = typeof params.search === 'string' ? params.search.trim() : '';
      const response = await apiClient.get(
        `${API_ENDPOINTS.FOLLOWERS_LIST}/${creatorId}/followerList`,
        { params: search ? { skip, limit, search } : { skip, limit } }
      );

      const responseData = response?.data;
      let followersArray =
        (Array.isArray(responseData?.data?.followers) && responseData.data.followers) ||
        (Array.isArray(responseData?.followers) && responseData.followers) ||
        (Array.isArray(responseData?.data) && responseData.data) ||
        (Array.isArray(responseData) && responseData) ||
        [];

      if (Array.isArray(followersArray) && followersArray.length > 0) {
        const profileKeys = followersArray
          .map((follower) => {
            const candidates = [
              follower?.profile_picture,
              follower?.profilePicture,
              follower?.profileImage,
              follower?.user?.profile_picture,
              follower?.user?.profilePicture,
              follower?.user?.profileImage,
            ];

            return candidates.find(
              (value) => typeof value === 'string' && value.trim() !== '' && !value.startsWith('http')
            );
          })
          .filter(Boolean);

        if (profileKeys.length > 0) {
          const uniqueKeys = [...new Set(profileKeys)];
          try {
            const signedUrlsData = await getSignedUrls(uniqueKeys, true);
            const signedUrlMap = {};
            signedUrlsData.forEach(({ key, signedUrl }) => {
              signedUrlMap[key] = signedUrl;
            });

            followersArray.forEach((follower) => {
              const applySignedUrl = (target, fieldNames) => {
                if (!target || typeof target !== 'object') return;
                fieldNames.forEach((field) => {
                  const value = target[field];
                  if (typeof value === 'string' && value.trim() !== '' && !value.startsWith('http')) {
                    if (signedUrlMap[value]) {
                      target[field] = signedUrlMap[value];
                    }
                  }
                });
              };

              applySignedUrl(follower, ['profile_picture', 'profilePicture', 'profileImage']);
              if (follower?.user) {
                applySignedUrl(follower.user, ['profile_picture', 'profilePicture', 'profileImage']);
              }
            });
          } catch (signError) {
            reportError(signError, 'signed profile pictures followers');
          }
        }
      }

      return {
        success: true,
        status: response.status || 200,
        message: responseData?.message || 'Followers list fetched successfully',
        data: followersArray,
        pagination: responseData?.pagination || responseData?.data?.pagination || null,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        },
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch followers list');
    }
  }
}

export default new FollowService();

